interface LogoProps {
  size?: 'small' | 'medium' | 'large'
  showText?: boolean
}

function Logo({ size = 'medium', showText = false }: LogoProps) {
  const sizeClasses = {
    small: 'w-8 h-8',
    medium: 'w-16 h-16',
    large: 'w-32 h-32'
  }

  const textSizes = {
    small: 'text-lg',
    medium: 'text-2xl',
    large: 'text-5xl',
  }

  return (
    <div className={`flex items-center ${showText ? 'gap-2' : ''}`}>
      <div className={`${sizeClasses[size]} relative flex-shrink-0`}>
        <svg viewBox="0 0 120 120" className="w-full h-full">
          <defs>
            <linearGradient id="logoGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#6366f1" />
              <stop offset="100%" stopColor="#8b5cf6" />
            </linearGradient>
            <linearGradient id="swordBlade" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#c0c4cc" />
              <stop offset="40%" stopColor="#e8eaed" />
              <stop offset="100%" stopColor="#9ca0a8" />
            </linearGradient>
            <linearGradient id="swordHandle" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#4b5060" />
              <stop offset="100%" stopColor="#363a45" />
            </linearGradient>
          </defs>

          {/* Quantum orbital ring (background) */}
          <circle cx="60" cy="60" r="54" fill="none" stroke="url(#logoGrad)" strokeWidth="2.5" opacity="0.3" />

          {/* Left sword (bottom-left to top-right) */}
          <g transform="rotate(-45 60 60)">
            {/* Blade */}
            <polygon points="60,8 56,14 56,62 60,8 64,62 64,14" fill="url(#swordBlade)" />
            <line x1="60" y1="10" x2="60" y2="60" stroke="#d1d5db" strokeWidth="0.8" opacity="0.6" />
            {/* Guard */}
            <rect x="49" y="62" width="22" height="5" rx="1.5" fill="#6366f1" />
            <polygon points="47,64 49,62 49,67 47,65" fill="#4f46e5" />
            <polygon points="73,64 71,62 71,67 73,65" fill="#4f46e5" />
            {/* Handle */}
            <rect x="56" y="67" width="8" height="22" rx="2" fill="url(#swordHandle)" />
            <line x1="60" y1="69" x2="60" y2="87" stroke="#555b66" strokeWidth="1" opacity="0.5" />
            {/* Pommel */}
            <circle cx="60" cy="92" r="5" fill="#4b5060" />
            <circle cx="60" cy="92" r="2.5" fill="#6366f1" opacity="0.6" />
          </g>

          {/* Right sword (bottom-right to top-left) */}
          <g transform="rotate(45 60 60)">
            {/* Blade */}
            <polygon points="60,8 56,14 56,62 60,8 64,62 64,14" fill="url(#swordBlade)" />
            <line x1="60" y1="10" x2="60" y2="60" stroke="#d1d5db" strokeWidth="0.8" opacity="0.6" />
            {/* Guard */}
            <rect x="49" y="62" width="22" height="5" rx="1.5" fill="#8b5cf6" />
            <polygon points="47,64 49,62 49,67 47,65" fill="#7c3aed" />
            <polygon points="73,64 71,62 71,67 73,65" fill="#7c3aed" />
            {/* Handle */}
            <rect x="56" y="67" width="8" height="22" rx="2" fill="url(#swordHandle)" />
            <line x1="60" y1="69" x2="60" y2="87" stroke="#555b66" strokeWidth="1" opacity="0.5" />
            {/* Pommel */}
            <circle cx="60" cy="92" r="5" fill="#4b5060" />
            <circle cx="60" cy="92" r="2.5" fill="#8b5cf6" opacity="0.6" />
          </g>

          {/* Quantum nucleus (center, on top of swords intersection) */}
          <circle cx="60" cy="60" r="10" fill="#0f0f23" />
          <circle cx="60" cy="60" r="9" fill="none" stroke="url(#logoGrad)" strokeWidth="1.5" />
          <circle cx="60" cy="60" r="4" fill="#6366f1" />
          <circle cx="60" cy="60" r="2" fill="#a5b4fc" opacity="0.7" />

          {/* Orbital arcs (over the swords) */}
          <ellipse cx="60" cy="60" rx="26" ry="10" fill="none" stroke="#8b5cf6" strokeWidth="1.8" transform="rotate(45 60 60)" opacity="0.7" />
          <ellipse cx="60" cy="60" rx="26" ry="10" fill="none" stroke="#6366f1" strokeWidth="1.8" transform="rotate(-45 60 60)" opacity="0.7" />

          {/* Electron dots on orbitals */}
          <circle cx="78" cy="42" r="3" fill="#a5b4fc" />
          <circle cx="42" cy="42" r="3" fill="#c4b5fd" />
        </svg>
      </div>
      {showText && (
        <span className={`${textSizes[size]} font-bold bg-gradient-to-r from-qcloud-primary to-qcloud-secondary bg-clip-text text-transparent`}>
          QuantumArena
        </span>
      )}
    </div>
  )
}

export default Logo
